import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../services/firebase';
import { collection, query, where, orderBy, limit, getDocs, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { getAppointmentColors } from '../../utils/appointmentColors';

const Dashboard = () => {
  const { currentUser } = useAuth();
  const [appointments, setAppointments] = useState([]);

  const [estimates, setEstimates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentWeather, setCurrentWeather] = useState(null);
  const [weatherLoading, setWeatherLoading] = useState(true);
  const [appointmentWeather, setAppointmentWeather] = useState({});
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);

  // Stats
  const [todayAppointments, setTodayAppointments] = useState(0);
  const [pendingEstimates, setPendingEstimates] = useState(0);
  const [totalAppointments, setTotalAppointments] = useState(0);
  const [totalEstimates, setTotalEstimates] = useState(0);

  useEffect(() => {
    if (currentUser) {
      loadUserData();
      loadCurrentWeather();
    }
  }, [currentUser]);

  // Handle window resize for responsive layout
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadUserData = async () => {
    try {
      // Load ALL appointments first to get total count
      const allAppointmentsQuery = query(
        collection(db, 'appointments'),
        where('userId', '==', currentUser.uid)
      );
      const allAppointmentsSnapshot = await getDocs(allAppointmentsQuery);
      const allAppointmentsData = allAppointmentsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        date: doc.data().date,
        time: doc.data().time
      }));
      
      // Sort appointments by date (most recent first) and set counts
      const sortedAppointments = allAppointmentsData.sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        return dateB - dateA;
      });
      console.log('Loaded appointments:', allAppointmentsData.length, allAppointmentsData);
      setTotalAppointments(allAppointmentsData.length);
      setAppointments(sortedAppointments.slice(0, 5));

      // Load ALL estimates first to get total count
      const allEstimatesQuery = query(
        collection(db, 'estimates'),
        where('userId', '==', currentUser.uid)
      );
      const allEstimatesSnapshot = await getDocs(allEstimatesQuery);
      const allEstimatesData = allEstimatesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        submittedDate: doc.data().createdAt?.toDate().toLocaleDateString() || 'Unknown'
      }));
      
      // Sort estimates by creation date (most recent first) and set counts
      const sortedEstimates = allEstimatesData.sort((a, b) => {
        const dateA = a.createdAt?.toDate() || new Date(0);
        const dateB = b.createdAt?.toDate() || new Date(0);
        return dateB - dateA;
      });
      console.log('Loaded estimates:', allEstimatesData.length, allEstimatesData);
      setTotalEstimates(allEstimatesData.length);
      setEstimates(sortedEstimates.slice(0, 5));

      // Calculate stats
      const today = new Date().toDateString();
      const todayAppts = allAppointmentsData.filter(apt => 
        new Date(apt.date).toDateString() === today && apt.status !== 'cancelled'
      ).length;
      setTodayAppointments(todayAppts);

      const pendingEsts = allEstimatesData.filter(est => est.status === 'pending').length;
      setPendingEstimates(pendingEsts);

      setLoading(false);
    } catch (error) {
      console.error('Error loading user data:', error);
      setLoading(false);
    }
  };

  const loadCurrentWeather = async () => {
    const API_KEY = process.env.REACT_APP_WEATHER_API_KEY;
    if (!API_KEY) {
      setWeatherLoading(false);
      return;
    }

    try {
      let weatherLocation = 'New York'; // Default fallback
      let useCoordinates = false;
      let lat, lon;
      
      // Try to get user's location from their profile
      if (currentUser) {
        try {
          const userRef = doc(db, 'users', currentUser.uid);
          const userDoc = await getDoc(userRef);
          if (userDoc.exists()) {
            const userData = userDoc.data();
            if (userData.address && userData.address.trim()) {
              // Extract city from address (assuming format like "123 Main St, City, State, ZIP")
              const addressParts = userData.address.split(',');
              if (addressParts.length >= 2) {
                weatherLocation = addressParts[1].trim(); // Get city part
              }
            } else {
              // No address in profile, try browser geolocation
              try {
                const position = await new Promise((resolve, reject) => {
                  navigator.geolocation.getCurrentPosition(resolve, reject, {
                    timeout: 5000,
                    enableHighAccuracy: false
                  });
                });
                lat = position.coords.latitude;
                lon = position.coords.longitude;
                useCoordinates = true;
              } catch (geoError) {
                console.warn('Geolocation not available, using default location');
              }
            }
          }
        } catch (error) {
          console.warn('Could not get user location, using default:', error);
        }
      }

      let apiUrl;
      if (useCoordinates) {
        apiUrl = `https://api.openweathermap.org/data/2.5/weather?lat=${lat}&lon=${lon}&appid=${API_KEY}&units=imperial`;
      } else {
        apiUrl = `https://api.openweathermap.org/data/2.5/weather?q=${weatherLocation}&appid=${API_KEY}&units=imperial`;
      }

      const response = await fetch(apiUrl);
      
      if (response.ok) {
        const data = await response.json();
        setCurrentWeather({
          temp: Math.round(data.main.temp),
          description: data.weather[0].description,
          icon: data.weather[0].icon,
          city: data.name,
          humidity: data.main.humidity,
          windSpeed: Math.round(data.wind.speed), // Already in mph with imperial units
          location: useCoordinates ? 'Your Location' : weatherLocation,
          isUserLocation: useCoordinates || (weatherLocation !== 'New York')
        });
      }
    } catch (error) {
      console.error('Error loading weather:', error);
    } finally {
      setWeatherLoading(false);
    }
  };

  const getWeatherIcon = (iconCode) => {
    const iconMap = {
      '01d': '◯', '01n': '◐',
      '02d': '◑', '02n': '◒',
      '03d': '◒', '03n': '◒',
      '04d': '◒', '04n': '◒',
      '09d': '◦', '09n': '◦',
      '10d': '◦', '10n': '◦',
      '11d': '◈', '11n': '◈',
      '13d': '◊', '13n': '◊',
      '50d': '◌', '50n': '◌'
    };
    return iconMap[iconCode] || '◯';
  };

  if (loading) {
    return (
      <Layout>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '60vh' 
        }}>
          <div className="modern-loading-spinner"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={{ 
        padding: windowWidth <= 480 ? '0.75rem' : windowWidth <= 768 ? '1rem' : '1.5rem',
        maxWidth: '100%',
        width: '100%',
        margin: '0',
        minHeight: '100vh',
        overflowX: 'hidden',
        boxSizing: 'border-box'
      }}>
        {/* Header */}
        <div style={{ 
          marginBottom: windowWidth <= 768 ? 'var(--space-4)' : 'var(--space-6)', 
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: windowWidth <= 768 ? 'flex-start' : 'center',
          flexDirection: windowWidth <= 640 ? 'column' : 'row',
          gap: windowWidth <= 640 ? 'var(--space-3)' : '0',
          padding: windowWidth <= 640 ? '0' : windowWidth <= 768 ? '0' : '0',
          maxWidth: '100%',
          width: '100%',
          overflowX: 'hidden',
          boxSizing: 'border-box',
          borderBottom: '1px solid #e2e8f0',
          paddingBottom: windowWidth <= 768 ? 'var(--space-4)' : 'var(--space-5)'
        }}>
          <div>
            <h1 style={{ 
              fontSize: windowWidth <= 640 ? '1.5rem' : '2rem',
              fontWeight: '700',
              color: '#1e293b',
              margin: '0 0 var(--space-2) 0',
              letterSpacing: '-0.01em'
            }}>
              Dashboard
            </h1>
            <p style={{ 
              fontSize: windowWidth <= 640 ? '0.875rem' : '1rem',
              color: '#64748b',
              margin: 2
            }}>
              Welcome back, <span style={{ fontWeight: '500', color: '#475569' }}>{currentUser?.displayName || currentUser?.email?.split('@')[0] || 'Client'}</span>
            </p>
          </div>
        </div>

        {/* Weather Widget */}
        <div className="modern-card" style={{ 
          marginBottom: windowWidth <= 768 ? 'var(--space-4)' : 'var(--space-6)',
          margin: windowWidth <= 640 ? '0.5rem 0.5rem var(--space-4) 0.5rem' : windowWidth <= 768 ? '0.75rem 0.75rem var(--space-4) 0.75rem' : '1rem 1rem var(--space-6) 1rem',
          background: 'linear-gradient(to right, #f8fafc, #f1f5f9)',
          color: 'var(--gray-900)',
          border: '1px solid var(--gray-200)',
          maxWidth: '100%',
          overflowX: 'hidden',
          boxSizing: 'border-box',
          borderRadius: '10px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{ 
            padding: windowWidth <= 768 ? 'var(--space-4)' : 'var(--space-6)',
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: windowWidth <= 480 ? 'flex-start' : 'center',
            flexDirection: windowWidth <= 480 ? 'column' : 'row',
            gap: windowWidth <= 480 ? 'var(--space-4)' : '0',
            position: 'relative',
            zIndex: 1,
            borderLeft: '4px solid #94a3b8',
            paddingLeft: 'var(--space-5)'
          }}>
            <div style={{ flex: windowWidth <= 480 ? 'none' : '1' }}>
              <h3 style={{ 
                margin: '0 0 var(--space-2) 0',
                fontSize: windowWidth <= 640 ? '1.125rem' : '1.25rem',
                fontWeight: '600',
                color: '#334155'
              }}>
                Current Weather
              </h3>
              <p style={{ 
                margin: 0,
                fontSize: windowWidth <= 640 ? '0.8rem' : '0.875rem',
                color: '#64748b'
              }}>
                {currentWeather && currentWeather.isUserLocation 
                  ? `Weather in ${currentWeather.city}` 
                  : 'Perfect conditions for tree care'
                }
              </p>
            </div>
            {currentWeather && (
              <div style={{ 
                textAlign: windowWidth <= 480 ? 'left' : 'right',
                display: 'flex',
                alignItems: 'center',
                gap: windowWidth <= 480 ? 'var(--space-4)' : '0',
                flexDirection: windowWidth <= 480 ? 'row' : 'column'
              }}>
                <div style={{ 
                  fontSize: windowWidth <= 640 ? '2rem' : '2.5rem', 
                  marginBottom: windowWidth <= 480 ? '0' : 'var(--space-2)',
                  color: '#334155'
                }}>
                  {getWeatherIcon(currentWeather.icon)}
                </div>
                <div>
                  <div style={{ 
                    fontSize: windowWidth <= 640 ? '1.25rem' : '1.5rem', 
                    fontWeight: '700',
                    color: '#334155'
                  }}>
                    {currentWeather.temp}°F
                  </div>
                  <div style={{ 
                    fontSize: windowWidth <= 640 ? '0.8rem' : '0.875rem',
                    textTransform: 'capitalize',
                    color: '#64748b'
                  }}>
                    {currentWeather.description}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: windowWidth <= 640 ? '1fr' : windowWidth <= 1024 ? 'repeat(3, 1fr)' : 'repeat(3, 1fr)',
          gap: windowWidth <= 640 ? '0.75rem' : '1rem',
          marginBottom: windowWidth <= 768 ? 'var(--space-4)' : 'var(--space-5)',
          margin: windowWidth <= 640 ? '0.5rem' : windowWidth <= 768 ? '0.75rem' : '1rem',
          padding: 0,
          maxWidth: '100%',
          width: '100%',
          boxSizing: 'border-box'
        }}>
          <div 
            className="metric-card-modern"
            style={{ 
              background: '#ffffff',
              color: 'var(--gray-900)',
              cursor: 'pointer',
              border: '1px solid var(--gray-200)',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
              borderRadius: '8px',
              padding: windowWidth <= 640 ? '1rem' : '1.25rem',
              transition: 'all 0.3s ease',
              position: 'relative',
              overflow: 'hidden'
            }}
            onClick={() => window.location.href = '/appointments'}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.05)';
            }}
          >
            <div style={{ position: 'relative', zIndex: 1, textAlign: windowWidth <= 480 ? 'center' : 'left' }}>
              <div style={{ 
                fontSize: windowWidth <= 480 ? '1.25rem' : windowWidth <= 640 ? '1.5rem' : '1.75rem',
                marginBottom: '0.35rem',
                color: 'var(--gray-600)'
              }}>📅</div>
              <div style={{ 
                fontSize: windowWidth <= 480 ? '1.5rem' : windowWidth <= 640 ? '1.75rem' : '2rem',
                fontWeight: '700',
                marginBottom: '0.15rem',
                color: 'var(--gray-900)'
              }}>{totalAppointments}</div>
              <div style={{ 
                fontSize: windowWidth <= 480 ? '0.7rem' : windowWidth <= 640 ? '0.75rem' : '0.8rem',
                fontWeight: '500',
                color: 'var(--gray-600)'
              }}>Total Appointments</div>
            </div>
          </div>

          <div 
            className="metric-card-modern"
            style={{ 
              background: '#ffffff',
              color: 'var(--gray-900)',
              cursor: 'pointer',
              border: '1px solid var(--gray-200)',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
              borderRadius: '8px',
              padding: windowWidth <= 640 ? '1rem' : '1.25rem',
              transition: 'all 0.3s ease',
              position: 'relative',
              overflow: 'hidden'
            }}
            onClick={() => window.location.href = '/estimates'}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.05)';
            }}
          >
            <div style={{ position: 'relative', zIndex: 1, textAlign: windowWidth <= 480 ? 'center' : 'left' }}>
              <div style={{ 
                fontSize: windowWidth <= 480 ? '1.25rem' : windowWidth <= 640 ? '1.5rem' : '1.75rem',
                marginBottom: '0.35rem',
                color: 'var(--gray-600)'
              }}>💰</div>
              <div style={{ 
                fontSize: windowWidth <= 480 ? '1.5rem' : windowWidth <= 640 ? '1.75rem' : '2rem',
                fontWeight: '700',
                marginBottom: '0.15rem',
                color: 'var(--gray-900)'
              }}>{totalEstimates}</div>
              <div style={{ 
                fontSize: windowWidth <= 480 ? '0.7rem' : windowWidth <= 640 ? '0.75rem' : '0.8rem',
                fontWeight: '500',
                color: 'var(--gray-600)'
              }}>Total Estimates</div>
            </div>
          </div>

          <div 
            className="metric-card-modern"
            style={{ 
              background: '#ffffff',
              color: 'var(--gray-900)',
              cursor: 'pointer',
              border: '1px solid var(--gray-200)',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
              borderRadius: '8px',
              padding: windowWidth <= 640 ? '1rem' : '1.25rem',
              transition: 'all 0.3s ease',
              position: 'relative',
              overflow: 'hidden'
            }}
            onClick={() => window.location.href = '/my-reviews'}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.05)';
            }}
          >
            <div style={{ position: 'relative', zIndex: 1, textAlign: windowWidth <= 480 ? 'center' : 'left' }}>
              <div style={{ 
                fontSize: windowWidth <= 480 ? '1.25rem' : windowWidth <= 640 ? '1.5rem' : '1.75rem',
                marginBottom: '0.35rem',
                color: 'var(--gray-600)'
              }}>⭐</div>
              <div style={{ 
                fontSize: windowWidth <= 640 ? '1rem' : '1.1rem',
                fontWeight: '600',
                marginBottom: '0.15rem',
                color: 'var(--gray-900)'
              }}>Mis Reseñas</div>
              <div style={{ 
                fontSize: windowWidth <= 480 ? '0.7rem' : windowWidth <= 640 ? '0.75rem' : '0.8rem',
                fontWeight: '500',
                color: 'var(--gray-600)'
              }}>Ver y Crear</div>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div style={{ 
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: windowWidth <= 480 ? 'var(--space-3)' : windowWidth <= 768 ? 'var(--space-4)' : 'var(--space-6)',
          marginBottom: windowWidth <= 768 ? 'var(--space-4)' : 'var(--space-6)',
          padding: 0,
          maxWidth: '100%',
          overflowX: 'hidden',
          boxSizing: 'border-box',
          width: '100%'
        }}>
          {/* Upcoming Appointments */}
          <div className="modern-card" style={{
            width: '100%',
            margin: '0 auto',
            boxSizing: 'border-box',
            borderTop: '4px solid #4338ca',
            boxShadow: '0 4px 12px rgba(79, 70, 229, 0.08)'
          }}>
            <div className="card-header-modern" style={{
              borderBottom: '1px solid #e0e7ff',
              paddingBottom: '0.75rem',
              background: 'rgba(79, 70, 229, 0.1)'
            }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: windowWidth <= 640 ? 'flex-start' : 'center',
                flexDirection: windowWidth <= 640 ? 'column' : 'row',
                gap: windowWidth <= 640 ? 'var(--space-3)' : '0'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                  <div style={{ 
                    fontSize: windowWidth <= 480 ? '1.25rem' : '1.5rem',
                    color: '#4338ca'
                  }}>
                    ⊡
                  </div>
                  <div>
                    <h3 style={{ 
                      margin: 0,
                      fontSize: windowWidth <= 480 ? '1rem' : '1.125rem',
                      fontWeight: '600',
                      color: '#4338ca'
                    }}>
                      Upcoming Appointments
                    </h3>
                    <p style={{ 
                      margin: 0,
                      fontSize: windowWidth <= 640 ? '0.8rem' : '0.875rem',
                      color: 'var(--gray-600)'
                    }}>
                      Your scheduled services
                    </p>
                  </div>
                </div>
                <a 
                  href="/appointments"
                  className="btn btn-primary btn-sm"
                  style={{ 
                    textDecoration: 'none',
                    fontSize: windowWidth <= 640 ? '0.8rem' : '0.875rem',
                    padding: windowWidth <= 640 ? '0.5rem 0.75rem' : '0.5rem 1rem',
                    width: windowWidth <= 640 ? '100%' : 'auto',
                    textAlign: 'center',
                    background: '#ffffff',
                    border: '1px solid var(--gray-300)',
                    color: 'var(--gray-900)',
                    borderRadius: '6px',
                    fontWeight: '500',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 1px 4px rgba(0, 0, 0, 0.05)',
                    display: 'inline-block'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 1px 4px rgba(0, 0, 0, 0.05)';
                  }}
                >
                  View All
                </a>
              </div>
            </div>
            
            <div className="card-body-modern">
              {appointments.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                  {appointments.slice(0, 3).map((appointment, index) => (
                    <div 
                      key={appointment.id}
                      className="list-item-modern"
                      style={{ borderBottom: '1px solid var(--gray-200)' }}
                      onClick={() => window.location.href = '/appointments'}
                    >
                      <div className="list-avatar-modern" style={{ 
                        background: appointment.status === 'confirmed' ? getAppointmentColors(appointment.service).primary : 
                                   appointment.status === 'pending' ? getAppointmentColors(appointment.service).primary : 
                                   '#9ca3af',
                        width: '14px',
                        height: '14px',
                        border: appointment.status === 'confirmed' ? `1px solid ${getAppointmentColors(appointment.service).primary}40` : 
                               appointment.status === 'pending' ? `1px solid ${getAppointmentColors(appointment.service).primary}40` : 
                               '1px solid rgba(156, 163, 175, 0.3)'
                      }} />
                      <div className="list-content-modern">
                        <div className="list-primary-modern" style={{
                          fontSize: windowWidth <= 480 ? '0.9rem' : '1rem',
                          fontWeight: '600',
                          color: getAppointmentColors(appointment.service).primary
                        }}>{appointment.service}</div>
                        <div className="list-secondary-modern" style={{
                          fontSize: windowWidth <= 480 ? '0.8rem' : '0.875rem'
                        }}>
                          {appointment.date} at {appointment.time}
                        </div>
                      </div>
                      
                      <div style={{
                        display: 'inline-block',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        fontWeight: '500',
                        textTransform: 'capitalize',
                        backgroundColor: appointment.status === 'confirmed' ? 'rgba(79, 70, 229, 0.1)' :
                                         appointment.status === 'pending' ? 'rgba(234, 179, 8, 0.1)' :
                                         'rgba(156, 163, 175, 0.1)',
                        color: appointment.status === 'confirmed' ? '#4f46e5' :
                               appointment.status === 'pending' ? '#b45309' :
                               '#4b5563'
                      }}>
                        {appointment.status}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="modern-empty-state">
                  <div style={{ fontSize: '2rem', color: '#94a3b8', marginBottom: '0.5rem' }}>⊡</div>
                  <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '1rem', fontWeight: '600', color: '#475569' }}>No upcoming appointments</h4>
                  <p style={{ margin: '0 0 1.5rem 0', fontSize: '0.875rem', color: '#64748b' }}>Schedule your next tree service</p>
                  <button
                    className="btn btn-primary"
                    style={{
                      background: '#ffffff',
                      border: '1px solid var(--gray-300)',
                      color: 'var(--gray-900)',
                      padding: '0.75rem 1.5rem',
                      borderRadius: '6px',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 1px 4px rgba(0, 0, 0, 0.05)'
                    }}
                    onClick={() => window.location.href = '/appointments'}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-1px)';
                      e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 1px 4px rgba(0, 0, 0, 0.05)';
                    }}
                  >
                    Book Service
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Recent Estimates */}
          <div className="modern-card" style={{
            width: '100%',
            margin: '0 auto',
            boxSizing: 'border-box',
            borderTop: '4px solid #059669',
            boxShadow: '0 4px 12px rgba(16, 185, 129, 0.08)'
          }}>
            <div className="card-header-modern" style={{
              borderBottom: '1px solid #d1fae5',
              paddingBottom: '0.75rem',
              background: 'rgba(16, 185, 129, 0.1)'
            }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: windowWidth <= 640 ? 'flex-start' : 'center',
                flexDirection: windowWidth <= 640 ? 'column' : 'row',
                gap: windowWidth <= 640 ? 'var(--space-3)' : '0'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                  <div style={{ 
                    fontSize: windowWidth <= 480 ? '1.25rem' : '1.5rem',
                    color: '#059669'
                  }}>
                    ◇
                  </div>
                  <div>
                    <h3 style={{ 
                      margin: 0,
                      fontSize: windowWidth <= 480 ? '1rem' : '1.125rem',
                      fontWeight: '600',
                      color: '#059669'
                    }}>
                      Recent Estimates
                    </h3>
                    <p style={{ 
                      margin: 0,
                      fontSize: windowWidth <= 640 ? '0.8rem' : '0.875rem',
                      color: 'var(--gray-600)'
                    }}>
                      Your quote requests
                    </p>
                  </div>
                </div>
                <a 
                  href="/estimates"
                  className="btn btn-sm"
                  style={{
                    background: '#ffffff',
                    border: '1px solid var(--gray-300)',
                    fontSize: windowWidth <= 640 ? '0.8rem' : '0.875rem',
                    padding: windowWidth <= 640 ? '0.5rem 0.75rem' : '0.5rem 1rem',
                    width: windowWidth <= 640 ? '100%' : 'auto',
                    textAlign: 'center',
                    color: 'var(--gray-900)',
                    textDecoration: 'none',
                    borderRadius: '6px',
                    fontWeight: '500',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 1px 4px rgba(0, 0, 0, 0.05)',
                    display: 'inline-block'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 1px 4px rgba(0, 0, 0, 0.05)';
                  }}
                >
                  View All
                </a>
              </div>
            </div>
            
            <div style={{ padding: '1.5rem' }}>
              {estimates.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {estimates.slice(0, 3).map((estimate, index) => (
                    <div 
                      key={estimate.id}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '1rem',
                        padding: '1rem',
                        background: 'rgba(16, 185, 129, 0.02)',
                        borderRadius: '8px',
                        border: '1px solid rgba(16, 185, 129, 0.1)',
                        transition: 'all 0.2s ease',
                        cursor: 'pointer'
                      }}
                      onClick={() => window.location.href = '/estimates'}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <div style={{
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        background: estimate.status === 'pending' ? '#eab308' : 
                                   estimate.status === 'quoted' ? '#10b981' : 
                                   estimate.status === 'approved' ? '#4f46e5' : '#6b7280',
                        flexShrink: 0,
                        marginTop: '0.25rem'
                      }} />
                      
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontWeight: '600',
                          fontSize: '1rem',
                          color: '#111827',
                          marginBottom: '0.25rem'
                        }}>
                          {estimate.service}
                        </div>
                        <div style={{
                          fontSize: '0.875rem',
                          color: '#6b7280',
                          marginBottom: '0.5rem',
                          lineHeight: '1.4'
                        }}>
                          {estimate.description.length > 80 
                            ? estimate.description.substring(0, 80) + '...' 
                            : estimate.description}
                        </div>
                        
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '1rem',
                          flexWrap: 'wrap'
                        }}>
                          {estimate.quoteAmount && (
                            <div style={{
                              fontSize: '0.875rem',
                              color: '#10b981',
                              fontWeight: '600',
                              background: 'rgba(16, 185, 129, 0.1)',
                              padding: '0.25rem 0.5rem',
                              borderRadius: '6px'
                            }}>
                              ${estimate.quoteAmount.toLocaleString()}
                            </div>
                          )}
                          
                          {estimate.messages && estimate.messages.length > 0 && (
                            <div style={{
                              fontSize: '0.75rem',
                              color: '#6b7280',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.25rem'
                            }}>
                              <span>✉</span>
                              <span>{estimate.messages.length} message{estimate.messages.length !== 1 ? 's' : ''}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                        gap: '0.25rem'
                      }}>
                        <div style={{
                          fontSize: '0.75rem',
                          fontWeight: '500',
                          textTransform: 'capitalize',
                          backgroundColor: estimate.status === 'pending' ? 'rgba(234, 179, 8, 0.1)' :
                                          estimate.status === 'quoted' ? 'rgba(16, 185, 129, 0.1)' :
                                          estimate.status === 'approved' ? 'rgba(79, 70, 229, 0.1)' :
                                          'rgba(156, 163, 175, 0.1)',
                          color: estimate.status === 'pending' ? '#b45309' :
                                 estimate.status === 'quoted' ? '#10b981' :
                                 estimate.status === 'approved' ? '#4f46e5' :
                                 '#4b5563',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          letterSpacing: '0.01em'
                        }}>
                          {estimate.status}
                        </div>
                        <div style={{
                          fontSize: '0.75rem',
                          color: '#9ca3af'
                        }}>
                          {estimate.submittedDate}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ 
                  textAlign: 'center',
                  padding: '3rem 1rem',
                  color: '#6b7280'
                }}>
                  <div style={{ 
                    fontSize: '2rem',
                    color: '#94a3b8',
                    marginBottom: '0.5rem'
                  }}>◇</div>
                  <h4 style={{
                    margin: '0 0 0.5rem 0',
                    fontSize: '1rem',
                    fontWeight: '600',
                    color: '#475569'
                  }}>
                    No estimates yet
                  </h4>
                  <p style={{
                    margin: '0 0 1.5rem 0',
                    fontSize: '0.875rem',
                    color: '#64748b'
                  }}>
                    Get a quote for your tree service needs
                  </p>
                  <button
                    style={{
                      background: '#ffffff',
                      color: 'var(--gray-900)',
                      border: '1px solid var(--gray-300)',
                      padding: '0.75rem 1.5rem',
                      borderRadius: '6px',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 1px 4px rgba(0, 0, 0, 0.05)'
                    }}
                    onClick={() => window.location.href = '/estimates'}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-1px)';
                      e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 1px 4px rgba(0, 0, 0, 0.05)';
                    }}
                  >
                    Request Estimate
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

      </div>
    </Layout>
  );
};

export default Dashboard;
